import { useCallback, useEffect, useState } from 'react';
import { isIos, isStandalone } from './usePushNotifications';

/** localStorage-nyckel: när användaren senast sa "inte nu". */
const DISMISS_KEY = 'pwa_install_dismissed_at';

/** Hur länge ett "inte nu" gäller innan erbjudandet får komma tillbaka. */
const DISMISS_DAYS = 30;

/*
 * `beforeinstallprompt` fångas på modulnivå, inte i en effekt.
 *
 * Chrome avfyrar händelsen tidigt i sidladdningen — ofta innan React har
 * monterat något. En lyssnare som registreras i en `useEffect` missar den då,
 * och eftersom händelsen bara kommer en gång per sidladdning finns inget sätt
 * att be om den igen. Modulen importeras när bunten evalueras, alltså före
 * första renderingen.
 */
let deferredPrompt = null;
let wasInstalled = false;
const subscribers = new Set();

/** Väck alla hooks som lyssnar, så de renderar om med nytt läge. */
function notify() {
  subscribers.forEach((fn) => fn());
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Utan preventDefault visar Chrome sin egen mini-infobar i stället, och
    // då kan vi inte välja när frågan ställs.
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    wasInstalled = true;
    notify();
  });
}

/**
 * True när användaren nyligen avvisat erbjudandet.
 *
 * @returns {boolean} True om avvisningen fortfarande gäller.
 */
function recentlyDismissed() {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    if (!at) return false;
    return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

/**
 * Läge och styrning för "installera appen".
 *
 * Två skilda vägar, eftersom plattformarna inte fungerar likadant:
 *
 * - **Android/Chromium** ger `beforeinstallprompt`, alltså en riktig
 *   installationsdialog vi kan trigga från en knapp.
 * - **iOS/Safari** har ingen sådan händelse över huvud taget. Där återstår att
 *   berätta hur man gör (Dela → Lägg till på hemskärmen). Det är inte en
 *   nödlösning utan enda vägen, och den behövs: på iOS kräver web push att
 *   sajten ligger på hemskärmen, så utan det steget går notiser inte att slå
 *   på alls.
 *
 * @returns {{
 *   canInstall: boolean,
 *   needsManualInstall: boolean,
 *   installed: boolean,
 *   dismissed: boolean,
 *   promptInstall: () => Promise<boolean>,
 *   dismiss: () => void
 * }} canInstall = installationsdialog finns att visa; needsManualInstall =
 *   användaren måste göra det själv (iOS); installed = körs redan som app.
 */
export default function useInstallPrompt() {
  const [, forceRender] = useState(0);
  const [dismissed, setDismissed] = useState(() => recentlyDismissed());

  useEffect(() => {
    const rerender = () => forceRender((n) => n + 1);
    subscribers.add(rerender);
    return () => {
      subscribers.delete(rerender);
    };
  }, []);

  const installed = isStandalone() || wasInstalled;

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      // Händelsen kan bara användas en gång — släng den oavsett utfall, annars
      // ser knappen ut att fungera men gör ingenting vid andra trycket.
      deferredPrompt = null;
      notify();
      return outcome === 'accepted';
    } catch {
      deferredPrompt = null;
      notify();
      return false;
    }
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* strunt samma — avvisningen överlever bara inte omladdning */
    }
    setDismissed(true);
  }, []);

  return {
    canInstall: Boolean(deferredPrompt) && !installed,
    needsManualInstall: isIos() && !installed,
    installed,
    dismissed,
    promptInstall,
    dismiss,
  };
}
